/**
 * Hero Video Controller
 *
 * Lazy-loads the hero video well after the page has loaded so the poster <img>
 * can be the LCP element without the video download competing for bandwidth.
 * Skips the video entirely on slow connections or data-saver mode to avoid
 * wasting bandwidth on a decorative asset.
 * Once the video starts playing it fades in over the poster image.
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["poster", "video"];

  connect() {
    const video = this.videoTarget;
    const src = video.dataset.src;

    if (!src) return;

    // Skip video on slow connections or when data-saver is enabled.
    const conn = navigator.connection || navigator.mozConnection;
    if (conn) {
      if (conn.saveData) return;
      const slow = ["slow-2g", "2g", "3g"];
      if (slow.includes(conn.effectiveType)) return;
    }

    // Wait for the page to fully load, then add extra delay so the video
    // download doesn't compete with LCP-critical resources.
    if (document.readyState === "complete") {
      setTimeout(() => this.#loadVideo(video, src), 3000);
    } else {
      window.addEventListener(
        "load",
        () => setTimeout(() => this.#loadVideo(video, src), 3000),
        { once: true }
      );
    }
  }

  #loadVideo(video, src) {
    video.src = src;

    video.addEventListener(
      "playing",
      () => {
        video.classList.remove("opacity-0");
        // Remove poster image after video transition completes.
        this.posterTarget.addEventListener(
          "transitionend",
          () => this.posterTarget.remove(),
          { once: true }
        );
        this.posterTarget.classList.add("opacity-0");
      },
      { once: true }
    );

    video.play().catch(() => {
      // Autoplay blocked — keep showing the poster image.
    });
  }
}
